import { BiCardView, BiLayoutItem } from '@euler/model/bi/dashboard';
import { memo, useMemo } from 'react';
import { View } from 'react-native';
import { biGridHeight, biItemRect } from '../../functions/biGridGeometry';
import {
  biCardFits,
  biFitGridRows,
  biGridBottom,
} from '../../functions/biGridLayout';
import type { BiRenderCard } from './DashboardGrid';
import { useBiFitNeeds } from './useBiFitNeeds';

/**
 * Desktop custom: the stored 12-column grid as placed, except that a card
 * fitting its content (a KPI group) is never cut short: it is drawn at its
 * content's height, at least its own, and when that needs more rows the
 * grid makes room (`biFitGridRows`).
 */
export const CustomGrid = memo(
  ({
    items,
    width,
    cards,
    renderCard,
  }: {
    items: BiLayoutItem[];
    width: number;
    cards: Map<string, BiCardView>;
    renderCard: BiRenderCard;
  }) => {
    const { needs, report } = useBiFitNeeds(width, cards);
    const drawn = useMemo(() => biFitGridRows(items, needs), [items, needs]);
    return (
      <View style={{ height: biGridHeight(biGridBottom(drawn)) }}>
        {drawn.map(item => {
          const card = cards.get(item.cardId);
          if (!card) return null;
          const rect = biItemRect(item, width);
          if (!biCardFits(card.viz))
            return (
              <View key={item.cardId} style={{ position: 'absolute', ...rect }}>
                {renderCard(card, rect.height)}
              </View>
            );
          // At its content's height, the slot's at the least: the card
          // reports what its content needs, not the stretched slot.
          const { height, ...at } = rect;
          return (
            <View
              key={item.cardId}
              style={{ position: 'absolute', ...at, minHeight: height }}
            >
              {renderCard(card, undefined, undefined, undefined, px =>
                report(item.cardId, px),
              )}
            </View>
          );
        })}
      </View>
    );
  },
);
